import * as React from 'react';

import { appIdentity } from '@/app/app-config';

/**
 * Browser tab identity: the title suffix and favicon. The shared app identity
 * applies unless a customer turns on the `browserIdentity` brand-scope switch.
 */
export interface BrowserIdentity {
  name: string;
  /** Customer PNG logo (data URL or same-origin URL); null keeps the app icons. */
  iconHref: string | null;
}

const SHARED_IDENTITY: BrowserIdentity = { name: appIdentity.name, iconHref: null };
const BrowserIdentityContext = React.createContext<BrowserIdentity>(SHARED_IDENTITY);

export function useBrowserIdentity(): BrowserIdentity {
  return React.useContext(BrowserIdentityContext);
}

/** Keeps `document.title` as `<title> · <identity name>` (WCAG 2.4.2). */
export function useDocumentTitle(title: string): void {
  const { name } = useBrowserIdentity();
  React.useEffect(() => {
    document.title = `${title} · ${name}`;
    return () => {
      document.title = appIdentity.name;
    };
  }, [title, name]);
}

const ICON_SELECTOR = 'link[rel~="icon"], link[rel="apple-touch-icon"]';

/** Points every favicon link at `href` and restores the original links on cleanup. */
export function useFaviconOverride(href: string | null): void {
  React.useLayoutEffect(() => {
    if (!href) return undefined;
    const links = Array.from(document.head.querySelectorAll<HTMLLinkElement>(ICON_SELECTOR));
    const previous = links.map(link => ({ link, href: link.getAttribute('href'), type: link.getAttribute('type') }));
    let added: HTMLLinkElement | null = null;
    if (links.length === 0) {
      added = document.createElement('link');
      added.rel = 'icon';
      document.head.appendChild(added);
      links.push(added);
    }
    for (const link of links) {
      link.setAttribute('href', href);
      link.setAttribute('type', 'image/png');
    }
    return () => {
      for (const item of previous) {
        if (item.href === null) item.link.removeAttribute('href');
        else item.link.setAttribute('href', item.href);
        if (item.type === null) item.link.removeAttribute('type');
        else item.link.setAttribute('type', item.type);
      }
      added?.remove();
    };
  }, [href]);
}

/** Supplies a customer identity to descendants; `null` keeps the shared app identity. */
export function BrowserIdentityProvider({ identity, children }: { identity: BrowserIdentity | null; children: React.ReactNode }) {
  const name = identity?.name.trim() || appIdentity.name;
  const iconHref = identity?.iconHref ?? null;
  const value = React.useMemo<BrowserIdentity>(
    () => identity ? { name, iconHref } : SHARED_IDENTITY,
    [identity, name, iconHref]
  );
  useFaviconOverride(value.iconHref);
  return <BrowserIdentityContext.Provider value={value}>{children}</BrowserIdentityContext.Provider>;
}
